import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const STORAGE_KEY = 'app_full_mode';
const STORAGE_EXPIRY_KEY = 'app_full_mode_expiry';
const EXPIRY_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Hook to check if the application is running in full mode
 * Full mode is enabled when URL contains ?mode=full parameter
 * The preference is stored in localStorage and persists for 24 hours
 * @returns {boolean} true if mode=full is present in URL or stored in localStorage
 */
export const useFullMode = (): boolean => {
  const [searchParams] = useSearchParams();
  const [isFullMode, setIsFullMode] = useState<boolean>(() => {
    // Initialize from localStorage on mount
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const expiry = localStorage.getItem(STORAGE_EXPIRY_KEY);
      
      if (stored && expiry) {
        const expiryTime = parseInt(expiry, 10);
        if (Date.now() < expiryTime) {
          return stored === 'true';
        }
        // Expired - clean up
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(STORAGE_EXPIRY_KEY);
      }
    } catch (error) {
      console.warn('Failed to read full mode from localStorage:', error);
    }
    return false;
  });

  useEffect(() => {
    const modeParam = searchParams.get('mode');
    
    if (modeParam !== null) {
      const newFullMode = modeParam === 'full';
      setIsFullMode(newFullMode);
      
      // Save to localStorage with expiry
      try {
        if (newFullMode) {
          const expiryTime = Date.now() + EXPIRY_DURATION_MS;
          localStorage.setItem(STORAGE_KEY, 'true');
          localStorage.setItem(STORAGE_EXPIRY_KEY, expiryTime.toString());
        } else {
          // If explicitly set to non-full mode, remove from storage
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(STORAGE_EXPIRY_KEY);
        }
      } catch (error) {
        console.warn('Failed to save full mode to localStorage:', error);
      }
    }
  }, [searchParams]);

  return isFullMode;
};
